/*
 * Sample support data (P6 tickets & complaints, and Client Connections /
 * Ticket API). Tickets arrive from the portal, email, phone, WhatsApp or
 * straight from the software the client uses, through the Support API.
 *
 * Plug and play: ONE SUPPORT TOKEN PER CLIENT CONNECTION. Support sets up the
 * client and its product account in the CRM, presses "Issue support token",
 * and the client's admin pastes two values into their product — the CRM base
 * URL and the token — then presses "Test connection". The token itself says
 * which client is calling, so there is no tenant-code mapping on our side.
 * It works the same for a tenant on a multi-tenant platform (DeeHr360 HRM,
 * Demulla LMS, Enterprise Accounting) and for a standalone application; the
 * connection just records the deployment type for reporting. A client on HRM
 * and LMS is one customer with two connections (one per product account).
 *
 * Every public update on an API ticket is pushed back by a signed webhook when
 * one is set (otherwise the product polls), so the reporter sees the reply
 * inside their product; client feedback (rating + comment) comes back the same
 * way and feeds CSAT.
 *
 * Customers are the shared ones in mock/customers.js and owners are staff
 * from masters.js, so a ticket here is the same ticket the Customer 360 shows.
 */
import { daysFromNow } from "@/helpers/crm";
import { slaPolicies, productById } from "@/mock/masters";
import { customerById } from "@/mock/customers";

// Datetimes relative to now, so SLA timers in the prototype are always live.
export const hoursAgo = (h) => new Date(Date.now() - h * 3600000).toISOString();
const addHours = (iso, h) => new Date(new Date(iso).getTime() + h * 3600000).toISOString();

/* ---------- Lists the company keeps under Settings (new masters) ---------- */

// Settings → Lookups → Ticket categories: shared ones plus each product's own.
export const ticketCategoryGroups = [
  { product_id: null, name: "All products", categories: ["General / how-to", "Access / login", "Bug / error", "Data correction", "Billing", "Performance"] },
  { product_id: 1, name: "DeeHr360 HRM", categories: ["Payroll", "Leave", "Statutory returns", "Employee self-service"] },
  { product_id: 2, name: "Demulla LMS", categories: ["Loan application", "Disbursement", "Repayments / collections", "Interest & penalties", "M-Pesa / STK integration", "Reports"] },
  { product_id: 4, name: "Standalone applications", categories: ["Integration", "Change request"] },
];
export const ticketCategories = [...new Set(ticketCategoryGroups.flatMap((g) => g.categories))];
// The categories offered for a product: its own first, then the shared ones.
export const categoriesFor = (productId) => {
  const p = productById(productId);
  const own = ticketCategoryGroups.find((g) => g.product_id === Number(productId)) ?? (p?.deployment === "standalone" ? ticketCategoryGroups.find((g) => g.product_id === 4) : null);
  return [...(own?.categories ?? []), ...ticketCategoryGroups[0].categories];
};

// Modules inside each product, as the platforms send them in the "module" field.
export const productModules = {
  1: ["Payroll", "Leave", "Statutory returns", "Employee self-service", "Recruitment", "Appraisals"],
  2: ["Loan application", "Credit appraisal", "Loan disbursement", "Repayments", "Collections", "Interest & penalties", "M-Pesa / STK", "Reports"],
  3: ["General ledger", "Payables", "Receivables", "e-TIMS"],
  4: ["Shipments", "Grower payments", "Export documents"],
  5: ["Payroll", "Leave", "Statutory returns"],
};

// Settings → Lookups → Complaint categories.
export const complaintCategories = ["Service quality", "Response time", "Product defect", "Billing dispute", "Staff conduct", "Implementation", "Data loss / privacy"];

export const ticketChannels = ["Portal", "Email", "Phone", "WhatsApp", "API"];

// The workflow (P2.1): which status may follow which.
export const ticketStatuses = [
  { key: "new", name: "New", tone: "info", next: ["open", "in_progress", "closed"] },
  { key: "open", name: "Open", tone: "primary", next: ["in_progress", "awaiting_customer", "resolved"] },
  { key: "in_progress", name: "In progress", tone: "primary", next: ["awaiting_customer", "resolved"] },
  { key: "awaiting_customer", name: "Awaiting customer", tone: "warning", next: ["in_progress", "resolved", "closed"] },
  { key: "resolved", name: "Resolved", tone: "success", next: ["closed", "reopened"] },
  { key: "closed", name: "Closed", tone: "secondary", next: ["reopened"] },
  { key: "reopened", name: "Reopened", tone: "danger", next: ["in_progress", "resolved"] },
];
export const ticketStatus = (key) => ticketStatuses.find((s) => s.key === key);
export const isOpenTicket = (t) => !["resolved", "closed"].includes(t.status);

// SLA hours per priority, read from the company's SLA policy (Settings → SLA). Business days are counted as 8 working hours.
export const slaHours = { critical: [1, 4], high: [4, 8], medium: [8, 24], low: [16, 56] };
export const slaPolicyFor = (priority) => slaPolicies.find((p) => p.priority === priority);

/* ---------- Client connections (Support API tokens) ---------- */

export const CRM_BASE_URL = "https://support.demulla.com";

export const apiScopes = [
  { key: "tickets:create", label: "Raise tickets" },
  { key: "tickets:read", label: "Read its own tickets, comments and status" },
  { key: "comments:write", label: "Reply on its tickets" },
  { key: "feedback:write", label: "Rate resolved tickets and reopen them" },
];

export const webhookEvents = ["ticket.status_changed", "ticket.comment.public", "ticket.resolved", "ticket.closed"];

export const appStatusTone = (s) => ({ active: "success", suspended: "warning", revoked: "danger", expired: "secondary" })[s] ?? "secondary";

// Where the client's admin pastes the CRM URL and token, per product.
export const configuredIn = (productId) =>
  ({
    1: "DeeHr360 → Company settings → Integrations → Demulla Support",
    2: "Demulla LMS → Tenant settings → Integrations → Demulla Support",
    3: "Enterprise Accounting → Settings → Integrations → Demulla Support",
  })[productId] ?? "The application's configuration file / admin settings (SUPPORT_URL, SUPPORT_TOKEN)";

const CNX = (id, customer_id, product_id, environment, status, extra = {}) => {
  const c = customerById(customer_id);
  const p = productById(product_id);
  const account = c?.product_accounts.find((a) => a.product_id === product_id && (a.deployment === "platform" || a.environment === (environment === "Test" ? "UAT" : "Production")));
  const live = environment === "Live";
  return {
    id,
    code: `CNX-${String(id).padStart(3, "0")}`,
    name: `${c?.name} · ${p?.short ?? p?.name}${live ? "" : " (test)"}`,
    customer_id,
    product_id,
    deployment: p?.deployment === "standalone" ? "standalone" : "platform_tenant", // as /ping reports it
    account: account ? account.tenant_code ?? account.address : null, // informational: tenant code or instance address
    account_address: account?.address ?? null,
    environment,
    status,
    token_prefix: live ? "crm_live_" : "crm_test_",
    token_last4: ["9f2a", "c81e", "4b7d", "e03c", "77aa", "d1f0", "0b5e", "5a19", "a3c4", "71be", "f60d", "2e88"][id - 1],
    issued_at: daysFromNow(-120),
    expires_on: daysFromNow(245),
    grace_until: null,
    scopes: ["tickets:create", "tickets:read", "comments:write", "feedback:write"],
    webhook_url: "", // optional — without it the product polls
    webhook_secret_last4: "7c1d",
    events: webhookEvents.slice(),
    ip_allowlist: [],
    rate_limit: 60,
    timestamp_window: 300,
    configured_in: configuredIn(product_id),
    last_test: null,
    last_used_at: hoursAgo(0.2),
    calls_24h: 0,
    error_rate: 0,
    created_by: 8,
    created_at: daysFromNow(-150),
    ...extra,
  };
};

export const connectedApps = [
  CNX(1, 3, 1, "Live", "active", {
    contact: { name: "Amina Juma", email: "amina.juma@coastlinefreight.co.ke", phone: "+254 733 551 204" },
    webhook_url: "https://coastline.deehr360.com/support/webhook", calls_24h: 610, error_rate: 2.1, last_test: hoursAgo(26 * 24),
  }),
  CNX(2, 1, 1, "Live", "active", {
    contact: { name: "Linda Mutiso", email: "linda.mutiso@savannahhotels.co.ke", phone: "+254 722 410 118" },
    calls_24h: 1240, error_rate: 0.4, created_at: daysFromNow(-400), last_test: hoursAgo(90 * 24),
  }),
  CNX(3, 9, 2, "Live", "active", {
    contact: { name: "Wanjiru Kamau", email: "ict@harambeesacco.com", phone: "+254 711 300 902" },
    webhook_url: "https://harambeesacco.demullalms.com/support/webhook", ip_allowlist: ["41.90.64.0/24"], rate_limit: 120, calls_24h: 1820, error_rate: 0.9,
  }),
  CNX(4, 19, 2, "Live", "active", {
    contact: { name: "Brian Kiprono", email: "ops@gelisa.co.ke", phone: "+254 701 220 413" },
    webhook_url: "https://gelisa.demullalms.com/support/webhook", calls_24h: 980, error_rate: 1.2,
    issued_at: daysFromNow(0), grace_until: hoursAgo(-18), created_at: daysFromNow(-470),
  }),
  CNX(5, 18, 1, "Live", "active", {
    contact: { name: "Mary Achieng", email: "payroll@pwanilogistics.com", phone: "+254 720 118 774" },
    ip_allowlist: ["197.248.10.40"], calls_24h: 2210, error_rate: 1.2,
  }),
  CNX(6, 2, 2, "Live", "active", {
    contact: { name: "Stella Oduya", email: "stella@jamiimfi.co.ke", phone: "+254 701 220 413" },
    webhook_url: "https://jamii.demullalms.com/support/webhook", calls_24h: 430, error_rate: 0.2,
  }),
  CNX(7, 2, 1, "Live", "suspended", {
    contact: { name: "Stella Oduya", email: "stella@jamiimfi.co.ke", phone: "+254 701 220 413" },
    last_used_at: hoursAgo(26 * 24), calls_24h: 0, suspended_reason: "Script on the client side looped and hit the rate limit — suspended by Faith Achieng until fixed",
  }),
  CNX(8, 21, 4, "Live", "active", {
    contact: { name: "Mary Atieno", email: "it@kilimofresh.co.ke", phone: "+254 733 551 204" },
    webhook_url: "https://erp.kilimofresh.co.ke/hooks/support", calls_24h: 210, error_rate: 0.9, created_at: daysFromNow(-290),
  }),
  CNX(9, 9, 5, "Live", "active", {
    contact: { name: "Wanjiru Kamau", email: "ict@harambeesacco.com", phone: "+254 711 300 902" },
    webhook_url: "https://hrm.harambeesacco.com/api/crm-webhook", calls_24h: 310, error_rate: 7.8, last_used_at: hoursAgo(1.5),
  }),
  CNX(10, 20, 2, "Test", "active", {
    contact: { name: "Samuel Cheruiyot", email: "it@mkopobora.co.ke", phone: "+254 712 004 551" },
    calls_24h: 34, error_rate: 0, created_at: daysFromNow(-4), issued_at: daysFromNow(-4), last_test: hoursAgo(70),
  }),
  CNX(11, 14, 1, "Live", "revoked", {
    contact: { name: "Fatma Said", email: "gm@nyalibeach.co.ke", phone: "+254 722 900 300" },
    last_used_at: hoursAgo(62 * 24), calls_24h: 0, revoked_reason: "Customer exited (EXT-0005) — token revoked on the exit checklist",
  }),
  CNX(12, 21, 4, "Test", "expired", {
    contact: { name: "Mary Atieno", email: "it@kilimofresh.co.ke", phone: "+254 733 551 204" },
    webhook_url: "https://uat.erp.kilimofresh.co.ke/hooks/support", expires_on: daysFromNow(-9), last_used_at: hoursAgo(9 * 24), calls_24h: 0,
  }),
];
export const appById = (id) => connectedApps.find((a) => a.id === Number(id));
export const connectionDeployment = (d) => ({ platform_tenant: { name: "Platform tenant", tone: "primary", icon: "iconoir-network" }, standalone: { name: "Standalone app", tone: "purple", icon: "iconoir-server" } })[d] ?? { name: d, tone: "secondary", icon: "iconoir-network" };
export const clientConnections = connectedApps;
export const connectionsFor = (customerId) => connectedApps.filter((a) => a.customer_id === Number(customerId));
// The connection that carries a given product account, if a token has been issued for it.
export const connectionForAccount = (customerId, productId) => connectedApps.find((a) => a.customer_id === Number(customerId) && a.product_id === Number(productId) && a.status !== "revoked");

/* ---------- Tickets ---------- */

// API tickets carry the reporter — the end user inside the product — and the module they were in.
const R = (name, email, role) => ({ name, email, role });
// [subject, customer, channel, app, category, product, priority, status, owner, created hours ago, responded after h, resolved after h, contact, api: { reporter, module, account }]
const ROWS = [
  ["Payroll run fails at PAYE step for March", 3, "API", 1, "Payroll", 1, "critical", "in_progress", 8, 6, 0.5, null, "Amina Juma", { reporter: R("Amina Juma", "amina.juma@coastlinefreight.co.ke", "HR Officer · payroll admin"), module: "Payroll" }],
  ["Leave approvals stuck for Nanyuki property staff", 1, "API", 2, "Leave", 1, "high", "open", 8, 3, 1, null, "Linda Mutiso", { reporter: R("Linda Mutiso", "linda.mutiso@savannahhotels.co.ke", "Group HR · company admin"), module: "Leave" }],
  ["Cannot log in after password reset", 6, "Portal", null, "Access / login", 1, "medium", "new", null, 2, null, null, "Dr. Paul Kamau"],
  ["Loan statement shows wrong interest for member M-11820", 9, "API", 3, "Interest & penalties", 2, "high", "awaiting_customer", 10, 20, 2, null, "Wanjiru Kamau", { reporter: R("Wanjiru Kamau", "credit@harambeesacco.com", "Credit officer"), module: "Interest & penalties" }],
  ["NSSF tier II rates not applied", 5, "Email", null, "Statutory returns", 1, "high", "open", 8, 30, 3, null, "Irene Nduta"],
  ["STK push repayments not posting to loans", 19, "API", 4, "M-Pesa / STK integration", 2, "critical", "reopened", 8, 50, 0.8, null, "Brian Kiprono", { reporter: R("Brian Kiprono", "ops@gelisa.co.ke", "Operations manager · tenant admin"), module: "Repayments", feedback: { rating: 2, comment: "Same problem is back with today's 08:00 batch — reopening.", reopen: true } }],
  ["How to add a new branch cost centre", 15, "Phone", null, "General / how-to", 1, "low", "resolved", 8, 72, 2, 20, "Purity Chege"],
  ["P10 export rejected by iTax", 18, "API", 5, "Statutory returns", 1, "medium", "in_progress", 10, 16, 1, null, "Mary Achieng", { reporter: R("Mary Achieng", "payroll@pwanilogistics.com", "Payroll officer"), module: "Statutory returns" }],
  ["Invoice VAT rounding differs by KES 1", 13, "Email", null, "Billing", 1, "low", "open", null, 40, null, null, "Martin Gitau"],
  ["Disbursement to bank account failed for 3 loans", 9, "API", 3, "Disbursement", 2, "medium", "resolved", 10, 96, 3, 30, "Wanjiru Kamau", { reporter: R("Wanjiru Kamau", "credit@harambeesacco.com", "Credit officer"), module: "Loan disbursement", feedback: { rating: 4, comment: "Fixed the same day. Would like an alert when a bank transfer bounces.", reopen: false } }],
  ["Leave balances doubled after migration", 3, "Phone", null, "Leave", 1, "high", "open", null, 12, null, null, "Amina Juma"],
  ["Payslip email bouncing for 12 staff", 16, "Email", null, "Payroll", 1, "medium", "awaiting_customer", 8, 60, 6, null, "Victor Mutua"],
  ["Housekeeping staff locked out of self-service", 1, "API", 2, "Access / login", 1, "high", "resolved", 8, 120, 1, 6, "Peter Mwangi", { reporter: R("Peter Mwangi", "hr.mara@savannahhotels.co.ke", "HR assistant"), module: "Employee self-service", feedback: { rating: 5, comment: "Quick fix, staff back in within the hour.", reopen: false } }],
  ["Penalty charged on loan repaid before due date", 19, "API", 4, "Interest & penalties", 2, "low", "in_progress", 10, 90, 10, null, "Faith Njoki", { reporter: R("Faith Njoki", "collections@gelisa.co.ke", "Collections officer"), module: "Interest & penalties" }],
  ["Portfolio-at-risk report timing out", 2, "API", 6, "Reports", 2, "medium", "open", 10, 28, 30, null, "Stella Oduya", { reporter: R("Stella Oduya", "stella@jamiimfi.co.ke", "COO"), module: "Reports" }],
  ["Grant codes missing in donor payroll report", 12, "Email", null, "Payroll", 1, "low", "closed", 10, 300, 4, 50, "Esther Wairimu"],
  ["Add 40 casual workers in bulk", 5, "Portal", null, "General / how-to", 1, "low", "new", null, 5, null, null, "Irene Nduta"],
  ["Duplicate tickets created from the HRM help widget", 18, "API", 5, "Bug / error", 1, "high", "resolved", 10, 200, 1, 7, "Mary Achieng", { reporter: R("Mary Achieng", "payroll@pwanilogistics.com", "Payroll officer"), module: "Employee self-service", feedback: { rating: 3, comment: "Resolved, but it took a week to find the cause.", reopen: false } }],
  ["Payroll approval email not received", 3, "Email", null, "Payroll", 1, "medium", "open", 8, 70, 4, null, "Amina Juma"],
  ["Loan application stuck at credit appraisal", 2, "Phone", null, "Loan application", 2, "high", "awaiting_customer", 10, 44, 2, null, "Stella Oduya"],
  ["Shift allowances missing on October payslips", 1, "API", 2, "Payroll", 1, "medium", "new", null, 0.6, null, null, "Linda Mutiso", { reporter: R("Linda Mutiso", "linda.mutiso@savannahhotels.co.ke", "Group HR · company admin"), module: "Payroll" }],
  ["P9 forms show previous employer", 13, "Email", null, "Statutory returns", 1, "medium", "closed", 8, 400, 5, 40, "Martin Gitau"],
  ["Bulk B2C disbursement timing out at 200 loans", 19, "API", 4, "Disbursement", 2, "high", "open", 8, 4, 0.7, null, "Brian Kiprono", { reporter: R("Brian Kiprono", "ops@gelisa.co.ke", "Operations manager · tenant admin"), module: "Loan disbursement" }],
  ["Grower payment batch export fails on special characters", 21, "API", 8, "Integration", 4, "medium", "in_progress", 10, 20, 2, null, "Mary Atieno", { reporter: R("John Mutua", "accounts@kilimofresh.co.ke", "Accounts clerk"), module: "Grower payments" }],
  ["Leave module slow after v3.8 upgrade", 9, "API", 9, "Performance", 5, "medium", "open", null, 9, null, null, "Wanjiru Kamau", { reporter: R("Agnes Chebet", "hr@harambeesacco.com", "HR officer"), module: "Leave" }],
];

export const tickets = ROWS.map(([subject, customer_id, channel, app_id, category, product_id, priority, status, owner_id, ago, resp, res, contact, api], i) => {
  const id = i + 1;
  const created_at = hoursAgo(ago);
  const [rH, resH] = slaHours[priority];
  const app = app_id ? appById(app_id) : null;
  const product = productById(product_id);
  return {
    id,
    number: `TKT-${String(400 + id).padStart(4, "0")}`,
    type_key: "customer_support", // ticket type (§10): every client ticket is a "Customer Support" ticket
    kind: "customer",
    subject,
    customer_id,
    contact,
    channel,
    app_id,
    deployment: app?.deployment ?? null,
    channel_label: app ? `API · ${product?.short ?? product?.name}` : channel,
    external_ref: app ? `${product?.code ?? "APP"}-${88120 + id * 37}` : channel === "Email" ? `<msg-${id}a7@mail>` : null,
    account: app?.account ?? null, // the product account the connection was issued for (informational)
    reporter: api?.reporter ?? null,
    module: api?.module ?? null,
    feedback: api?.feedback ? { ...api.feedback, at: hoursAgo(Math.max(0.2, ago - (res ?? ago) - 2)), by: api.reporter?.name } : null,
    category,
    product_id,
    priority,
    status,
    owner_id,
    team: "Service Desk",
    created_at,
    first_response_at: resp === null ? null : addHours(created_at, resp),
    response_due: addHours(created_at, rH),
    resolution_due: addHours(created_at, resH),
    resolved_at: res === null ? null : addHours(created_at, res),
    reopen_count: status === "reopened" ? 1 : 0,
    // Client feedback posted through the API (rating 1–5) is the ticket's CSAT; other channels use the survey.
    csat: api?.feedback ? api.feedback.rating : ["resolved", "closed"].includes(status) ? [5, 4, 3, 5, 4, 2][i % 6] : null,
    escalated: priority === "critical" && status !== "resolved",
  };
});
export const ticketById = (id) => tickets.find((t) => t.id === Number(id));

// Client feedback posted through POST /tickets/{ref}/feedback — feeds CSAT in Customer Success and support analytics.
export const ticketFeedback = tickets
  .filter((t) => t.feedback)
  .map((t) => ({ ticket_id: t.id, number: t.number, customer_id: t.customer_id, product_id: t.product_id, ...t.feedback }));

/*
 * SLA state for one clock: met / breached (already happened) or due / breaching
 * (still running). The clock stops while a ticket is awaiting the customer.
 */
export const slaState = (t, clock) => {
  const due = new Date(clock === "response" ? t.response_due : t.resolution_due).getTime();
  const doneAt = clock === "response" ? t.first_response_at : t.resolved_at;
  if (doneAt) return new Date(doneAt).getTime() <= due ? "met" : "breached";
  if (clock === "resolution" && t.status === "awaiting_customer") return "paused";
  return Date.now() > due ? "breached" : "due";
};
export const isBreaching = (t) => isOpenTicket(t) && (slaState(t, "response") === "breached" || slaState(t, "resolution") === "breached");

// "2h 10m left" / "5h 3m over": the running SLA timer.
export const slaClock = (iso) => {
  const mins = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  const a = Math.abs(mins);
  const txt = a >= 1440 ? `${Math.floor(a / 1440)}d ${Math.floor((a % 1440) / 60)}h` : `${Math.floor(a / 60)}h ${a % 60}m`;
  return mins >= 0 ? `${txt} left` : `${txt} over`;
};

/* ---------- Conversation ---------- */

// webhook: delivery of a public update back to the source app (API tickets only).
const THREADS = {
  1: [
    { v: "public", type: "app", author: "Amina Juma", h: 6, body: "Payroll run PR-2026-03 fails at step 4 (PAYE). Error: 'Band table not found for period'. 212 employees affected, salaries due Friday." },
    { v: "internal", type: "agent", author: "Faith Achieng", h: 5.6, body: "Reproduced on their tenant (DEE003). The March PAYE band table was not published to this tenant after the Finance Act update. Escalating to the HRM product team." },
    { v: "public", type: "agent", author: "Faith Achieng", h: 5.5, body: "Thanks Amina, we've reproduced it. Engineering is publishing the corrected PAYE bands; we'll update you within the hour.", webhook: { state: "delivered", code: 200, attempts: 1 } },
    { v: "internal", type: "system", author: "System", h: 4, body: "Escalated to Head of Support (critical ticket not resolved within 2 hours)." },
    { v: "public", type: "agent", author: "Faith Achieng", h: 2, body: "Bands are published. Please re-run step 4 and confirm totals; we are watching the run on our side.", webhook: { state: "retrying", code: 503, attempts: 3, next: "in 30 min" } },
    { v: "public", type: "app", author: "Amina Juma", h: 1.4, body: "Re-run started. Step 4 passed, step 6 (bank file) now shows a warning on 3 employees." },
    { v: "internal", type: "agent", author: "Grace Njeri", h: 1, body: "This customer is flagged Critical for churn. Please keep CSM Grace in copy and call Amina after the run completes." },
  ],
  2: [
    { v: "public", type: "app", author: "Linda Mutiso", h: 3, body: "Leave requests for the Nanyuki property have been pending approval since Monday. The approver shows as 'vacant'." },
    { v: "public", type: "agent", author: "Faith Achieng", h: 2, body: "Received. The Nanyuki GM position was vacated in HRM, so approvals have no one to route to. We'll re-route them to the Group HR desk by 3pm.", webhook: { state: "delivered", code: 200, attempts: 1 } },
  ],
  4: [
    { v: "public", type: "app", author: "Wanjiru Kamau", h: 20, body: "Member M-11820 statement shows interest KES 4,210 for September; expected 3,980 on the reducing balance." },
    { v: "public", type: "agent", author: "Samuel Kariuki", h: 18, body: "Interest was accrued on the balance before the June top-up was reversed. Can you confirm the reversal date with the member?", webhook: { state: "failed", code: 500, attempts: 6 } },
  ],
  6: [
    { v: "public", type: "app", author: "Brian Kiprono", h: 50, body: "Since yesterday 18:00 STK repayments show in M-Pesa but are not posting to loans. 46 borrowers affected; they are now showing as in arrears." },
    { v: "public", type: "agent", author: "Faith Achieng", h: 49.2, body: "We're on it. The C2B confirmation callbacks for their tenant (LMS003) are queueing behind a failed job. We'll replay them and update you.", webhook: { state: "delivered", code: 200, attempts: 1 } },
    { v: "public", type: "agent", author: "Faith Achieng", h: 30, body: "All 46 repayments replayed and posted; penalties raised overnight have been reversed.", webhook: { state: "delivered", code: 200, attempts: 1 } },
    { v: "public", type: "feedback", author: "Brian Kiprono", h: 3, body: "Rated 2/5 and reopened: \"Same problem is back with today's 08:00 batch — reopening.\" (12 repayments not posting so far)" },
  ],
};

export const commentsFor = (t) => {
  const thread = THREADS[t.id] ?? [
    { v: "public", type: t.app_id ? "app" : "customer", author: t.reporter?.name ?? t.contact, h: (Date.now() - new Date(t.created_at).getTime()) / 3600000, body: t.subject + ". Please assist." },
    ...(t.first_response_at
      ? [{ v: "public", type: "agent", author: "Faith Achieng", at: t.first_response_at, body: "Thanks for reporting this. We're looking into it and will update you shortly.", webhook: t.app_id ? { state: "delivered", code: 200, attempts: 1 } : null }]
      : []),
  ];
  if (t.feedback && !THREADS[t.id]) thread.push({ v: "public", type: "feedback", author: t.feedback.by, at: t.feedback.at, body: `Rated ${t.feedback.rating}/5${t.feedback.reopen ? " and reopened" : ""}: "${t.feedback.comment}"` });
  return thread.map((c, i) => ({ id: i + 1, visibility: c.v, author_type: c.type, author: c.author, body: c.body, at: c.at ?? hoursAgo(c.h), webhook: c.v === "public" && c.type === "agent" && t.app_id ? c.webhook ?? null : null }));
};

/* ---------- Complaints ---------- */

const CMP = (id, customer_id, category, subject, raised, owner_id, severity, status, extra = {}) => ({
  id,
  number: `CMP-${String(20 + id).padStart(4, "0")}`,
  customer_id,
  category,
  subject,
  raised_on: daysFromNow(-raised),
  raised_by: extra.raised_by ?? "Customer",
  owner_id,
  severity,
  status,
  root_cause: "",
  resolution: "",
  closed_on: null,
  escalation: null,
  ticket_ids: [],
  ...extra,
});

export const complaints = [
  CMP(1, 3, "Product defect", "Third payroll failure this quarter", 3, 7, "critical", "escalated", {
    raised_by: "Amina Juma (HR Officer)", ticket_ids: [1, 11, 19],
    root_cause: "Statutory tables not versioned per period; releases overwrite them.",
    escalation: { level: 2, to: "Head of Support → Head of Customer Success", at: daysFromNow(-2) },
  }),
  CMP(2, 3, "Response time", "Calls to support unanswered for 2 days", 12, 8, "high", "investigating", { raised_by: "Amina Juma (HR Officer)", ticket_ids: [11], root_cause: "Weekend phone rota gap." }),
  CMP(3, 19, "Service quality", "Repeated STK repayment posting failures", 9, 10, "high", "open", { raised_by: "Joyce Wanjala (CEO)", ticket_ids: [6, 23], escalation: { level: 1, to: "Support Team Lead", at: daysFromNow(-5) } }),
  CMP(4, 4, "Billing dispute", "Charged for 200 employees, using 140", 20, 11, "medium", "resolved", {
    raised_by: "Finance, Equator Pharmacies", root_cause: "Exited employees not removed from the licence count.", resolution: "Credit note CN-0044 for KES 78,000; subscription corrected.", closed_on: daysFromNow(-6),
  }),
  CMP(5, 12, "Implementation", "Donor reporting never configured", 40, 10, "medium", "closed", {
    raised_by: "Esther Wairimu", ticket_ids: [16], root_cause: "Requirement missed in implementation scope.", resolution: "Configured donor codes; customer still chose to exit.", closed_on: daysFromNow(-25),
  }),
  CMP(6, 6, "Staff conduct", "Support agent closed ticket without fixing", 15, 8, "low", "closed", {
    raised_by: "Dr. Paul Kamau", root_cause: "Agent closed on assumption; coached.", resolution: "Ticket reopened and fixed; apology call by Team Lead.", closed_on: daysFromNow(-10),
  }),
  CMP(7, 16, "Data loss / privacy", "Payslips emailed to wrong recipients", 4, 7, "critical", "investigating", {
    raised_by: "Victor Mutua (CFO)", ticket_ids: [12], escalation: { level: 2, to: "CTO / Data Protection Officer", at: daysFromNow(-3) },
  }),
  CMP(8, 14, "Service quality", "Slow resolution of shift-payroll issues", 95, 10, "medium", "closed", { raised_by: "Fatma Said (GM)", root_cause: "Shift rules needed a product change.", resolution: "Not resolved before exit.", closed_on: daysFromNow(-70) }),
];

/* ---------- API request log & webhook deliveries ---------- */

// Request log samples, per docs/support-api.md (paths under /api/v1/support, errors.code values from §8).
const ENDPOINTS = [
  ["GET", "/api/v1/support/ping", 200, 40],
  ["POST", "/api/v1/support/tickets", 201, 240],
  ["GET", "/api/v1/support/tickets?status=new,open,in_progress,awaiting_customer,reopened&per_page=50", 200, 120],
  ["GET", "/api/v1/support/tickets/TKT-0401", 200, 95],
  ["GET", "/api/v1/support/meta", 200, 60],
  ["POST", "/api/v1/support/tickets/TKT-0401/comments", 201, 180],
  ["POST", "/api/v1/support/tickets", 200, 60],
  ["POST", "/api/v1/support/tickets/TKT-0401/feedback", 409, 40],
  ["GET", "/api/v1/support/tickets/TKT-0399", 404, 30],
  ["POST", "/api/v1/support/tickets", 401, 15],
  ["GET", "/api/v1/support/tickets?updated_since=2026-10-20T08:15:00Z&sort=updated_at&per_page=100", 429, 5],
  ["POST", "/api/v1/support/tickets/TKT-0401/attachments", 413, 40],
  ["POST", "/api/v1/support/tickets", 422, 35],
  ["POST", "/api/v1/support/tickets/TKT-0401/feedback", 200, 90],
];

export const apiLogsFor = (app) =>
  Array.from({ length: 18 }, (_, i) => {
    const [method, endpoint, status, latency] = ENDPOINTS[(i * 5 + app.id) % ENDPOINTS.length];
    const ep = endpoint.replace("TKT-0401", tickets.find((t) => t.app_id === app.id)?.number ?? "TKT-0401");
    return {
      id: i + 1,
      at: hoursAgo(i * 0.7 + 0.1),
      method,
      endpoint: ep,
      status,
      latency_ms: latency + ((i * 37) % 90),
      ip: app.ip_allowlist[0]?.replace("/24", "").replace(/\.0$/, ".17") ?? "102.68.77.14",
      idempotency_key: method === "POST" && ep.endsWith("/tickets") ? `idem-${app.id}${(9000 + i * 131).toString(16)}` : null,
      request_id: `req_${(app.id * 100000 + i * 7177).toString(36)}`,
      error: { 401: "token_expired — ask Demulla support for a new token", 404: "not_found — no such ticket for this client", 409: "ticket_not_resolved — feedback is allowed once the ticket is resolved or closed", 413: "file_too_large — over 10 MB", 422: "validation_failed — category: 'loans' is not a category for this product", 429: `rate_limited — ${app.rate_limit}/min, Retry-After 20` }[status] ?? (method === "POST" && status === 200 && ep.endsWith("/tickets") ? "Idempotency-Key repeat — original ticket returned, nothing created" : null),
    };
  });

export const webhookDeliveriesFor = (app) => {
  const own = tickets.filter((t) => t.app_id === app.id);
  const states = [
    ["ticket.comment.public", 200, 1, "delivered"],
    ["ticket.status_changed", 200, 1, "delivered"],
    ["ticket.comment.public", 503, 3, "retrying"],
    ["ticket.resolved", 200, 2, "delivered"],
    ["ticket.status_changed", 200, 1, "delivered"],
    ["ticket.comment.public", 500, 6, "failed"],
    ["ticket.closed", 200, 1, "delivered"],
  ];
  return states.map(([event, code, attempts, state], i) => ({
    id: `whd_${(app.id * 1000 + i * 97).toString(36)}`,
    event,
    ticket: own[i % Math.max(own.length, 1)]?.number ?? "TKT-0401",
    at: hoursAgo(i * 3 + 0.3),
    code,
    attempts,
    state,
    duration_ms: code === 200 ? 180 + i * 23 : 3000,
    next_retry: state === "retrying" ? "in 30 min (retries 1m → 5m → 30m → 2h → 6h)" : null,
  }));
};
